import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  type DependencyList,
  type MutableRefObject,
} from "react";
import type { PollingProviderProps } from "./types";

const initialContextValue: Array<{
  id: string;
  fn: () => void;
  every: number;
  lastCall?: number;
}> = [];

const PollingContext = createContext<MutableRefObject<
  typeof initialContextValue
> | null>(null);

export const PollingProvider = (props: PollingProviderProps) => {
  const { children } = props;
  const ref = useRef<typeof initialContextValue>([]);
  useEffect(() => {
    const interval = setInterval(() => {
      for (let i = 0; i < ref.current.length; i++) {
        const registry = ref.current[i];
        const now = Number(new Date());
        if (!registry.lastCall || now - registry.lastCall >= registry.every) {
          registry.fn();
          registry.lastCall = now;
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <PollingContext.Provider value={ref}>{children}</PollingContext.Provider>
  );
};

export const useEvery = (fn, interval, deps: DependencyList = []) => {
  const context = useContext(PollingContext);
  const id = useId();
  const findAndRemove = () => {
    const index = context?.current.findIndex((i) => i.id === id);
    if (index !== undefined && index !== -1) {
      context?.current.splice(index, 1);
    }
  };
  useEffect(() => {
    if (!context) {
      throw `no context was found
      did you add the 'PollingProvider' or 'withPolling' to the app?`.replace(
        /^\s+/g,
        "",
      );
    }
    findAndRemove();
    context.current.push({
      id,
      every: interval,
      fn,
    });
    return findAndRemove;
  }, deps);
};

export const withPolling = (Component) => {
  const displayName =
    "withPolling(" +
    (Component.displayName || Component.name || "Component") +
    ")";

  const wrapper = (props) => {
    return (
      <PollingProvider>
        <Component {...props} />
      </PollingProvider>
    );
  };

  wrapper.displayName = displayName;
  wrapper.getInitialProps = Component.getInitialProps || undefined;

  return wrapper;
};
